import React from "react";
import { useHistory } from "react-router";
import { useMappedState } from "redux-react-hook";

import { Table } from "src/core/dashboard/components/widgets";
import { IOrder } from "src/core/orders/api/orders.api";
import { OrderActionsList } from "src/core/orders/components/OrderActionsList";
import {
  Layout,
  MainContentWrapper
} from "src/core/shared/components/Layout";
import { adminOrderTableNewColumns } from "src/core/shared/components/tables/adminOrderTableColumns";
import useTerminology from "src/core/shared/hooks/useTerminology";
import { IStore } from "src/core/shared/store";
import { LoadingSpinner } from "src/library/components/LoadingSpinner";
import { ITableInstance } from "src/library/components/Tables/Table";
import {
  FilterParams,
  PaginatedParams
} from "src/library/utils";
import { useCallOffApi } from "src/modules/call-off/api/call-off.api";
import { PageProps } from "src/pages/Router/routes";

const defaultPagination: PaginatedParams<IOrder> = {
  page: 0,
  limit: 20,
  offset: 0,
  sort: [ [ "updatedAt", "DESC" ] ]
};

const CallOffsRelatedOrderPage: React.ReactFCWithChildren<PageProps<unknown>> = props => {
  const callOffId = props.match && props.match.params.callOffId ? parseInt(props.match.params.callOffId, 10) : null;
  const terminology = useTerminology();
  const history = useHistory();

  const {
    orderStatuses, role, erp, installedModules
  } = useMappedState((store: IStore) => ({
    orderStatuses: store.global.settings ? store.global.settings.orderStatuses : [],
    role: store.global.role,
    erp: store.global.settings && store.global.settings.erp.name,
    installedModules: store.global.settings?.installedModules || []
  }));

  const [ getOrdersByCallOffRes, getOrdersByCallOffReq ] = useCallOffApi("LIST_ORDER_BY_CALL_OFFS");
  const [ getCallOffRes, getCallOffReq ] = useCallOffApi("GET_CALL_OFF");
  const TableRef = React.useRef<ITableInstance<IOrder>>(null);

  React.useEffect(() => {
    if (callOffId) {
      getCallOffReq({ id: callOffId });
    }
  }, [ callOffId, getCallOffReq ]);

  const callOffDescription = React.useMemo(() => {
    const data = getCallOffRes.data;

    return `${data?.name} (${data?.customer?.accountNumber} - ${data?.product?.partNumber})`;
  }, [ getCallOffRes.data ]);

  const onFetchOrdersData = React.useCallback(
    (params: { filters: FilterParams<IOrder>, pagination: PaginatedParams<IOrder> }) => {
      if (callOffId) {
        getOrdersByCallOffReq({
          callOffId,
          filters: params.filters,
          pagination: params.pagination
        });
      }
    },
    [ callOffId, getOrdersByCallOffReq ]
  );

  const columns = React.useMemo(() => {
    if (terminology) {
      return adminOrderTableNewColumns({
        pricesVisible: true,
        installedModules,
        terminology,
        orderStatuses,
        actionButton: info => {
          if (erp && role) {
            return (
              <OrderActionsList
                erp={erp}
                terminology={terminology}
                role={role}
                order={info.row.original}
              />
            );
          }

          return null;
        }
      });
    }

    return null;
  }, [
    erp,
    installedModules,
    orderStatuses,
    role,
    terminology
  ]);

  if (!terminology) {
    return null;
  }

  return (
    <Layout {...props}>
      <MainContentWrapper fill>

        {getCallOffRes.pending ? <LoadingSpinner type="circular" /> :
        columns && (

          <Table
            tableOf={`${terminology.order.plural.text} for ${callOffDescription}`}
            loading={getOrdersByCallOffRes.pending}
            data={getOrdersByCallOffRes.data?.result ? getOrdersByCallOffRes.data.result : []}
            ref={TableRef}
            pageCount={getOrdersByCallOffRes.data?.pages}
            rowCount={getOrdersByCallOffRes.data?.count}
            columns={columns}
            onFetchData={onFetchOrdersData}
            defaultPagination={defaultPagination}
            error={getOrdersByCallOffRes.error}
            mainAction={row => history.push(`/orders/${row.original.id}`)}
          />

        )}
      </MainContentWrapper>
    </Layout>
  );
};

export default CallOffsRelatedOrderPage;